import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, Pressable, TextInput, ScrollView,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import Colors from '../../../constants/Colors';
import { assigneeName } from '../data';
import TicketAssigneeIcon from './TicketAssigneeIcon';
import DeleteIcon24 from '../../../components/24pxIcons/DeleteIcon24';
import SearchIcon from '../../../components/SearchIcon';
import updateAssignee from '../../../requests/Tickets/UpdateAssignee';
import TicketGroupTag from './TicketGroupTag';
import isMobile from '../../../components/IsMobile';
import SelectionModal from '../../../components/SelectionModal';
import Style from '../../../constants/Style';
import PressableStyled, { PressableState } from '../../../components/PressableStyled';
import PlusIcon from '../../../components/16pxIcons/PlusIcon';
import { logEvent } from '../../../components/LogEvent';
import {
  Ticket, TicketStatus, UserGroup, UserInstance, UserPermission,
} from '../../../constants/Types';

interface TicketAssigneeProps {
  selectedTicket: Ticket,
  users: UserInstance[],
  showAssigneeDropdown: boolean,
  setShowAssigneeDropdown: Dispatch<SetStateAction<boolean>>,
  assigneeSearchValue: string,
  setAssigneeSearchValue: Dispatch<SetStateAction<string>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setShowSpinner: Dispatch<SetStateAction<boolean>>,
  userGroups: UserGroup[],
  updateSelectedTicketInList,
}

const TicketAssignee = ({
  selectedTicket, users, showAssigneeDropdown, setShowAssigneeDropdown, userGroups,
  assigneeSearchValue, setAssigneeSearchValue, setLoading, setShowSpinner,
  updateSelectedTicketInList,
}: TicketAssigneeProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateTickets: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE);

  const AssigneeList = () => <View testID='assignee-list'>
    {users.filter((user: UserInstance) => (user.firstName ?? user.email)
      .toLowerCase().includes(assigneeSearchValue.toLowerCase()))
      .map((user: UserInstance) => {
        const disabled: boolean = selectedTicket?.assignee === user.id;

        const update: () => void = () => {
          setShowSpinner(true);
          updateAssignee(selectedTicket.id, user.id).then(() => {
            const updatedTicket: Ticket = { ...selectedTicket };
            if (selectedTicket.status !== TicketStatus.INPROGRESS) {
              updatedTicket.status = TicketStatus.INPROGRESS;
              updatedTicket.startedOn = Moment().toString();
            }
            updatedTicket.assignee = user.id;
            updateSelectedTicketInList(updatedTicket);
            setLoading(true);
          }).finally(() => setShowSpinner(false));
          setShowAssigneeDropdown(false);
        };

        return <Pressable
          key={`user_${user.id}`}
          {...{ disabled }}
          style={({ hovered, pressed }: PressableState) => [
            styles.item, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
            disabled && styles.itemSelected,
          ]}
          onPress={update}
        >
          <View style={styles.subContainer}>
            <TicketAssigneeIcon title={user.firstName ? `${user.firstName ?? user.email} ${user.lastName ?? ''}` : user.email} />
            <Text style={[styles.name, disabled && styles.nameSelected]}>
              {(user.firstName || user.lastName)
                ? `${user.firstName ?? ''} ${user.lastName ?? ''}` : user.email}
            </Text>
          </View>
          {/* @ts-ignore */}
          {user.UserGroupIds?.length > 0
            && <View style={styles.groupTagsContainer}>
              {/* @ts-ignore */}
              {user.UserGroupIds.map((groupId: string, gKey: number) => <TicketGroupTag
                {...{ groupId, userGroups }}
                key={`ticket_group_tag_${gKey}`}
              />)}
            </View>}
        </Pressable>;
      })}
  </View>;

  return <>
    {selectedTicket.assignee ? <View style={Style.row}>
      <TicketAssigneeIcon title={assigneeName(selectedTicket.assignee, users)} />
      <Text style={[styles.name, { flex: 1 }]}>
        {assigneeName(selectedTicket.assignee, users)}
      </Text>
      {canUpdateTickets && selectedTicket.status !== TicketStatus.COMPLETED && <PressableStyled
        onPress={() => {
          logEvent('TicketAssignee', 'delete_assignee');
          setShowAssigneeDropdown(!showAssigneeDropdown);
        }}
        testID='ticket-delete-assignee-btn'
        style={styles.deleteIcon}
      >
        <DeleteIcon24 />
      </PressableStyled>}
    </View> : <PressableStyled
      style={styles.addAssignee}
      onPress={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
      testID='add-assignee-button'
    >
      <PlusIcon color={styles.addAssigneeTitle.color} />
      <Text style={styles.addAssigneeTitle}>{'Add assignee'}</Text>
    </PressableStyled>}
    {isMobile ? <SelectionModal
      title={`${selectedTicket?.assignee ? 'Change' : 'Add'} assignee`}
      listContent={<AssigneeList />}
      isVisible={showAssigneeDropdown}
      hide={() => setShowAssigneeDropdown(false)}
    /> : (showAssigneeDropdown && <View style={styles.dropdown}>
      <View style={styles.dropdownPointer} />
      <Pressable style={styles.searchBox}>
        <SearchIcon />
        <TextInput
          style={styles.textInput}
          placeholderTextColor={Colors.light.hereworksPrimaryGrey700}
          placeholder='Search'
          onChangeText={setAssigneeSearchValue}
        />
      </Pressable>
      <ScrollView nestedScrollEnabled>
        <AssigneeList />
      </ScrollView>
    </View>)}
  </>;
};

const styles = StyleSheet.create({
  subContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  name: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    marginStart: 8,
  },
  groupTagsContainer: {
    flexWrap: 'wrap',
    flexDirection: 'row',
    flex: 1,
    marginTop: 4,
    marginLeft: 32,
  },
  dropdown: {
    position: 'absolute',
    height: 200,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    zIndex: 2,
    end: 0,
    top: 44,
  },
  dropdownPointer: {
    backgroundColor: Colors.light.hereworksBlue100,
    width: 12,
    height: 12,
    borderRadius: 4,
    position: 'absolute',
    top: -6,
    end: 22,
    zIndex: 1,
    transform: [{ rotateZ: '45deg' }],
  },
  searchBox: {
    backgroundColor: '#fff',
    zIndex: 2,
    borderRadius: 12,
    flexDirection: 'row',
    paddingHorizontal: 10,
    paddingVertical: 8,
    alignItems: 'center',
  },
  textInput: {
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    fontSize: 14,
    marginStart: 8,
    outlineWidth: 0,
  },
  item: {
    padding: 10,
    borderRadius: 4,
  },
  itemSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  nameSelected: {
    color: '#FFF',
  },
  addAssigneeTitle: {
    color: Colors.light.hereworksBlue400,
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    marginStart: 8,
  },
  deleteIcon: {
    marginStart: 8,
  },
  addAssignee: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default TicketAssignee;
